<template>
  <section class="open-projects" :class="{ 'open-offers': isOffers }" tabindex="-1" aria-labelledby="open-projects-title" :aria-busy="loading">
    <div class="panel-heading">
      <div>
        <h2 id="open-projects-title"><span class="status-dot" aria-hidden="true"></span>Offene {{ entryLabel }}</h2>
        <p>{{ isOffers ? 'Blaue Projekte aller Unternehmen: Angebote ohne Bestellung.' : 'Gelbe Projekte aller Unternehmen: Anfragen ohne Angebot oder Bestellung.' }}</p>
      </div>
      <button type="button" :disabled="isOpening" @click="$emit('close')">Schließen</button>
    </div>
    <div class="toolbar">
      <label class="search-field">
        <span>{{ entryLabel }} durchsuchen</span>
        <input v-model="search" type="search" placeholder="Projekt, Unternehmen, Kundennummer oder Benutzer" :disabled="isOpening">
      </label>
      <button type="button" :disabled="loading || isOpening" @click="loadProjects">Aktualisieren</button>
    </div>
    <p v-if="error" class="error-message" role="alert">{{ error }}</p>
    <p v-else-if="loading" class="empty-message" role="status">Offene {{ entryLabel }} werden geladen …</p>
    <template v-else>
      <p class="result-count" role="status">{{ filteredProjects.length }} von {{ projects.length }} offenen {{ isOffers ? 'Angeboten' : 'Anfragen' }}</p>
      <div v-if="filteredProjects.length" class="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Projekt</th>
              <th scope="col">Unternehmen</th>
              <th scope="col">Benutzer</th>
              <th scope="col">{{ isOffers ? 'Angeboten am' : 'Angefragt am' }}</th>
              <th scope="col"><span class="sr-only">Aktion</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="project in filteredProjects" :key="project.id">
              <td class="project-name">{{ project.name }}</td>
              <td>
                <span>{{ project.unternehmen?.name || 'Kein Unternehmen zugeordnet' }}</span>
                <small v-if="project.unternehmen?.kdnr">Kdnr. {{ project.unternehmen.kdnr }}</small>
                <small v-if="project.unternehmen?.plzort">{{ project.unternehmen.plzort }}</small>
              </td>
              <td>
                <span>{{ project.shop_user?.name || 'Kein Benutzer zugeordnet' }}</span>
                <small v-if="project.shop_user?.email">{{ project.shop_user.email }}</small>
              </td>
              <td class="request-date">{{ formatDateTime(project[dateField]) }}</td>
              <td class="action-cell">
                <button
                  type="button"
                  class="open-project-button"
                  :disabled="isOpening"
                  :aria-label="`Projekt ${project.name} öffnen`"
                  @click="$emit('open-project', project)"
                >{{ openingProjectId === project.id ? 'Wird geöffnet …' : 'Öffnen' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty-message">{{ search.trim() ? `Keine passenden offenen ${entryLabel} gefunden.` : `Aktuell gibt es keine offenen ${entryLabel}.` }}</p>
    </template>
  </section>
</template>

<script>
import api from '@/api/client';

export default {
  name: 'OpenProjectsComponent',
  props: {
    status: { type: String, default: 'yellow', validator: value => ['yellow', 'blue'].includes(value) },
    openingProjectId: { type: [Number, String], default: null },
  },
  emits: ['close', 'open-project', 'loaded'],
  data() {
    return { projects: [], search: '', loading: false, error: '' };
  },
  computed: {
    isOffers() {
      return this.status === 'blue';
    },
    entryLabel() {
      return this.isOffers ? 'Angebote' : 'Anfragen';
    },
    dateField() {
      return this.isOffers ? 'offer_sent_at' : 'request_sent_at';
    },
    isOpening() {
      return this.openingProjectId !== null;
    },
    filteredProjects() {
      const query = this.search.trim().toLocaleLowerCase('de');
      if (!query) return this.projects;
      return this.projects.filter(project => [
        project.name,
        project.unternehmen?.name,
        project.unternehmen?.kdnr,
        project.unternehmen?.plzort,
        project.shop_user?.name,
        project.shop_user?.email,
      ].some(value => String(value || '').toLocaleLowerCase('de').includes(query)));
    },
  },
  mounted() {
    this.loadProjects();
  },
  methods: {
    async loadProjects() {
      if (this.loading || this.isOpening) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await api.get('/api/projekte', { params: { status: this.status } });
        this.projects = [...(Array.isArray(response.data) ? response.data : [])].sort((a, b) =>
          String(b[this.dateField] || '').localeCompare(String(a[this.dateField] || '')) || b.id - a.id
        );
        this.$emit('loaded');
      } catch (error) {
        this.projects = [];
        this.error = error?.response?.data?.message || `Die offenen ${this.entryLabel} konnten nicht geladen werden. Bitte erneut versuchen.`;
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '–';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '–' : date.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.open-projects {
  margin: 0 1.8rem 1.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  color: #333;
  text-align: left;
  overflow: hidden;
}
.panel-heading, .toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 24px;
}
.panel-heading { background: #fafafa; border-bottom: 1px solid #e5e5e5; }
h2 { display: flex; align-items: center; gap: 10px; margin: 0; font-size: 20px; }
.panel-heading p { margin: 8px 0 0; color: #666; font-size: 14px; }
.status-dot { width: 12px; height: 12px; border-radius: 3px; background: #f1b64b; }
.open-offers .status-dot { background: #2f80c9; }
.search-field { flex: 1; max-width: 580px; }
.search-field span { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 600; }
input { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #aaa; border-radius: 5px; font: inherit; }
button { padding: 9px 14px; border: 1px solid #aaa; border-radius: 5px; background: #fff; color: #444; font: inherit; cursor: pointer; }
button:hover:not(:disabled) { background: #f3f3f3; border-color: #555; }
button:disabled { opacity: 0.5; cursor: default; }
button:focus-visible, input:focus-visible { outline: 2px solid #555; outline-offset: 3px; }
.toolbar { align-items: flex-end; }
.result-count { margin: 0 24px 14px; font-size: 13px; color: #666; }
.table-scroll { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 14px; }
th { padding: 12px 24px; background: #f7f7f7; color: #666; font-size: 13px; font-weight: 600; }
td { padding: 16px 24px; border-top: 1px solid #e5e5e5; vertical-align: top; }
tbody tr:hover { background: #fffdf7; }
.open-offers tbody tr:hover { background: #f4f9fd; }
.project-name { font-weight: 600; min-width: 130px; }
small { display: block; margin-top: 5px; color: #666; font-size: 12px; overflow-wrap: anywhere; }
.request-date, .action-cell { white-space: nowrap; }
.action-cell { text-align: right; }
.empty-message, .error-message { margin: 0; padding: 20px 24px 28px; }
.error-message { color: #a32424; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
@media (max-width: 760px) {
  .open-projects { margin-right: 0.5rem; margin-left: 0.5rem; }
  .panel-heading, .toolbar { padding: 16px; flex-wrap: wrap; gap: 12px; }
  .search-field { flex-basis: 100%; }
  th, td { padding: 12px 16px; }
}
</style>
